import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MediaFolderDto } from "shared";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export function useMediaFolders() {
	const { id: companyID } = useCompany();

	return useQuery<MediaFolderDto[]>({
		queryKey: QueryKeys.Files.Folders(companyID),
		queryFn: async () => (await Api.GetMediaFolders()).data,
	});
}

export function useCreateFolder() {
	const queryClient = useQueryClient();
	const { id: companyID } = useCompany();

	return useMutation({
		mutationFn: async (name: string) => {
			const res = await Api.CreateMediaFolder({ name });
			return res.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Files.Folders(companyID),
			});
		},
	});
}

export function useRenameFolder() {
	const queryClient = useQueryClient();
	const { id: companyID } = useCompany();

	return useMutation({
		mutationFn: async ({ id, name }: { id: number; name: string }) => {
			const res = await Api.RenameMediaFolder(id, { name });
			return res.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Files.Folders(companyID),
			});
		},
	});
}

export function useDeleteFolder() {
	const queryClient = useQueryClient();
	const { id: companyID } = useCompany();

	return useMutation({
		mutationFn: async (id: number) => {
			await Api.DeleteMediaFolder(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Files.Folders(companyID),
			});
			queryClient.invalidateQueries({
				queryKey: ["COMPANY", companyID, "FILES", "PAGE"],
			});
		},
	});
}

export function useMoveFiles() {
	const queryClient = useQueryClient();
	const { id: companyID } = useCompany();

	return useMutation({
		mutationFn: async ({
			fileIds,
			folderId,
		}: {
			fileIds: number[];
			folderId: number | null;
		}) => {
			await Api.MoveFiles({ fileIds, folderId });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: QueryKeys.Files.Folders(companyID),
			});
			queryClient.invalidateQueries({
				queryKey: ["COMPANY", companyID, "FILES", "PAGE"],
			});
		},
	});
}
